import React from 'react';
import { CheckCircle2, Circle, Loader2, XCircle, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '@/lib/utils';

export const APP_BUILD_STEPS = [
  { id: 'plan', title: 'Understand your prompt' },
  { id: 'codegen', title: 'Generate application code' },
  { id: 'build', title: 'Compile live preview' },
  { id: 'preview', title: 'Preview ready' },
];

export const INITIAL_BUILD_PROGRESS = {
  currentStepId: null,
  completedSteps: [],
  failedSteps: [],
};

export function advanceBuildProgress(prev, event) {
  const next = {
    currentStepId: prev?.currentStepId ?? null,
    completedSteps: [...(prev?.completedSteps || [])],
    failedSteps: [...(prev?.failedSteps || [])],
  };
  const complete = (id) => {
    if (!next.completedSteps.includes(id)) next.completedSteps.push(id);
  };
  const fail = (id) => {
    if (!next.failedSteps.includes(id)) next.failedSteps.push(id);
  };

  switch (event.type) {
    case 'start':
      return { currentStepId: 'plan', completedSteps: [], failedSteps: [] };
    case 'plan':
      complete('plan');
      next.currentStepId = 'codegen';
      return next;
    case 'codegen':
      complete('plan');
      complete('codegen');
      next.currentStepId = 'build';
      return next;
    case 'build':
      complete('plan');
      complete('codegen');
      complete('build');
      next.currentStepId = 'preview';
      return next;
    case 'preview':
      APP_BUILD_STEPS.forEach((s) => complete(s.id));
      return { currentStepId: null, completedSteps: APP_BUILD_STEPS.map((s) => s.id), failedSteps: [] };
    case 'error':
      if (next.currentStepId) fail(next.currentStepId);
      return next;
    default:
      return next;
  }
}

export function progressFromProjectState(project, isGenerating) {
  if (!project) return INITIAL_BUILD_PROGRESS;
  const state = project.state;
  if (state === 'error') {
    return { currentStepId: null, completedSteps: ['plan'], failedSteps: ['codegen'] };
  }
  if (state === 'live' || project.preview_html) {
    return {
      currentStepId: null,
      completedSteps: APP_BUILD_STEPS.map((s) => s.id),
      failedSteps: [],
    };
  }
  if (!isGenerating) return INITIAL_BUILD_PROGRESS;
  if (state === 'planning') {
    return { currentStepId: 'plan', completedSteps: [], failedSteps: [] };
  }
  if (state === 'generating') {
    return { currentStepId: 'codegen', completedSteps: ['plan'], failedSteps: [] };
  }
  if (state === 'testing') {
    return { currentStepId: 'build', completedSteps: ['plan', 'codegen'], failedSteps: [] };
  }
  return { currentStepId: 'codegen', completedSteps: ['plan'], failedSteps: [] };
}

export default function BuildStepTracker({ steps, currentStepId, completedSteps, failedSteps, compact = false }) {
  const doneCount = completedSteps.length;
  const visible = steps.length > 0 && (currentStepId || doneCount > 0 || failedSteps.length > 0);
  if (!visible) return null;

  return (
    <Card
      className={cn(
        'border-violet-500/20 bg-violet-500/[0.04] shadow-none',
        compact ? 'mb-0' : '',
      )}
      data-testid="app-builder-build-step-tracker"
    >
      <CardHeader className={cn('pb-2', compact ? 'px-3 py-2' : 'px-4 py-3')}>
        <div className="flex items-center gap-2">
          <Zap className="h-3.5 w-3.5 text-violet-400" />
          <CardTitle className="text-xs font-semibold text-violet-200">Build progress</CardTitle>
          <span className="ml-auto text-[10px] text-zinc-500">
            {doneCount}/{steps.length} done
          </span>
        </div>
      </CardHeader>
      <CardContent className={cn(compact ? 'px-3 pb-3 pt-0' : 'space-y-2 px-4 pb-4 pt-0')}>
        <div className={cn(compact ? 'flex flex-wrap items-center gap-2' : 'space-y-2')} data-testid="app-builder-build-step-list">
        {steps.map((step) => {
          const isActive = step.id === currentStepId;
          const isDone = completedSteps.includes(step.id);
          const isFailed = failedSteps.includes(step.id);
          return (
            <div
              key={step.id}
              className={cn(
                compact
                  ? 'flex min-w-0 items-center gap-2 rounded-full border px-2.5 py-1 text-[11px] transition-opacity'
                  : 'flex items-start gap-2.5 text-xs transition-opacity',
                isDone || isActive ? 'opacity-100' : 'opacity-50',
                compact && isDone && 'border-emerald-500/30 bg-emerald-500/10',
                compact && isFailed && 'border-red-500/30 bg-red-500/10',
                compact && isActive && !isDone && !isFailed && 'border-violet-500/30 bg-violet-500/10',
                compact && !isDone && !isFailed && !isActive && 'border-white/10 bg-white/[0.03]',
              )}
              data-testid={`build-step-${step.id}`}
            >
              <div
                className={cn(
                  compact ? 'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border' : 'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                  isDone && 'border-emerald-500/50 bg-emerald-500/15',
                  isFailed && 'border-red-500/50 bg-red-500/15',
                  isActive && !isDone && !isFailed && 'border-violet-500/50 bg-violet-500/15',
                  !isDone && !isFailed && !isActive && 'border-white/10 bg-white/[0.03]',
                )}
              >
                {isDone ? (
                  <CheckCircle2 className="h-2.5 w-2.5 text-emerald-400" />
                ) : isFailed ? (
                  <XCircle className="h-2.5 w-2.5 text-red-400" />
                ) : isActive ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin text-violet-400" />
                ) : (
                  <Circle className="h-2 w-2 text-zinc-600" />
                )}
              </div>
              <span
                className={cn(
                  'leading-5',
                  isDone && 'text-emerald-300',
                  isFailed && 'text-red-300',
                  isActive && !isDone && 'text-violet-200',
                  !isDone && !isFailed && !isActive && 'text-zinc-500',
                )}
              >
                {step.title}
              </span>
            </div>
          );
        })}
        </div>
      </CardContent>
    </Card>
  );
}
